import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import apiClient from 'src/helpers/apiClient';
import { getFormattedErr } from 'src/helpers';
import type {
  CreateCampaignBody,
  CreateSequenceFromAudienceBody,
  EnrollProspectsBody,
  IngestReplyBody,
  MarkEnrollmentBody,
  UpdateCampaignBody,
} from 'src/openapi';

export const listCampaigns = createAsyncThunk(
  'outreachCampaign/list',
  async (args: { status?: string } | undefined, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiListCampaigns({ status: args?.status });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);

export const getCampaign = createAsyncThunk(
  'outreachCampaign/get',
  async (campaignId: string, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiGetCampaign({ campaignId });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const createCampaign = createAsyncThunk(
  'outreachCampaign/create',
  async (body: CreateCampaignBody, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiCreateCampaign({ createCampaignBody: body });
      enqueueSnackbar('Campaign created', { variant: 'success' });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const updateCampaign = createAsyncThunk(
  'outreachCampaign/update',
  async (
    args: { campaignId: string; body: UpdateCampaignBody },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiUpdateCampaign({
        campaignId: args.campaignId,
        updateCampaignBody: args.body,
      });
      enqueueSnackbar('Campaign saved', { variant: 'success' });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const setCampaignStatus = createAsyncThunk(
  'outreachCampaign/setStatus',
  async (args: { campaignId: string; status: string }, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiSetCampaignStatus({
        campaignId: args.campaignId,
        setCampaignStatusBody: { status: args.status },
      });
      enqueueSnackbar(`Campaign ${args.status.toLowerCase()}`, {
        variant: 'success',
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const deleteCampaign = createAsyncThunk(
  'outreachCampaign/delete',
  async (campaignId: string, { rejectWithValue }) => {
    try {
      await (
        await apiClient.getOutreachApi()
      ).outreachApiDeleteCampaign({ campaignId });
      enqueueSnackbar('Campaign deleted', { variant: 'success' });
      return { campaignId };
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const enrollProspects = createAsyncThunk(
  'outreachCampaign/enroll',
  async (
    args: { campaignId: string; body: EnrollProspectsBody },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiEnrollProspects({
        campaignId: args.campaignId,
        enrollProspectsBody: args.body,
      });
      enqueueSnackbar(
        `Enrolled ${res.data.enrolled} (skipped ${res.data.skipped_existing} already in)`,
        { variant: 'success' },
      );
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const listEnrollments = createAsyncThunk(
  'outreachCampaign/listEnrollments',
  async (
    args: {
      campaignId: string;
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiListEnrollments({
        campaignId: args.campaignId,
        page: args.page,
        pageSize: args.pageSize,
        search: args.search,
        status: args.status,
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);

export const getEnrollmentHistory = createAsyncThunk(
  'outreachCampaign/enrollmentHistory',
  async (enrollmentId: string, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiEnrollmentHistory({ enrollmentId });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);

export const markEnrollment = createAsyncThunk(
  'outreachCampaign/markEnrollment',
  async (
    args: { enrollmentId: string; body: MarkEnrollmentBody },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiMarkEnrollment({
        enrollmentId: args.enrollmentId,
        markEnrollmentBody: args.body,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const createSequenceFromAudience = createAsyncThunk(
  'outreachCampaign/createFromAudience',
  async (body: CreateSequenceFromAudienceBody, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiCreateSequenceFromAudience({
        createSequenceFromAudienceBody: body,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const ingestReply = createAsyncThunk(
  'outreachCampaign/ingestReply',
  async (body: IngestReplyBody, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOutreachApi()
      ).outreachApiInboundReply({ ingestReplyBody: body });
      const classifiedNote = res.data.classification
        ? ` — classified: ${res.data.classification}`
        : '';
      enqueueSnackbar(
        `Matched ${res.data.matched} enrollment(s)${classifiedNote}`,
        { variant: 'success' },
      );
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);
